// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import type { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react';
import type { MessageEnvelope } from 'core/sockets/WebSocket';
import { waitForSocketMessage } from 'shared/utils/waitForSocketMessage';

const customBaseQuery = fetchBaseQuery({
  baseUrl: `${Application.env.VITE_API_BASE_URL}/`,
  prepareHeaders: (headers, { getState }) => {
    headers.set('Authorization', `Bearer ${(getState() as any).user.token?.value || ''}`);
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');
    headers.set('X-Client-Version', Application.env.VITE_IMAGE_VERSION);

    return headers;
  },
});

type SocketMessage = { correlationId: string; correlationMessageType: string; entityId: number };

const asyncBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  let result = await customBaseQuery(args, api, extraOptions);

  if (result.meta?.response?.status === 202) {
    if (isAsyncOperation(result.data)) {
      const {
        data,
        data: { correlationId, resourceUri },
      } = result;

      const waitTask = new Promise<void>((resolve) =>
        waitForSocketMessage(
          (e: Array<MessageEnvelope<SocketMessage>>) => e.some((x) => x.body.correlationId === correlationId),
          1000 * 60 * 5,
        ).finally(resolve),
      );

      const enhancedResult: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta> = {
        meta: result.meta,
        data: {
          ...data,
          resourcePromise: new LazyPromise(async (resolve, reject) => {
            try {
              await waitTask;
              const resource = await customBaseQuery(resourceUri, api, extraOptions);
              if (resource.error) {
                reject(resource.error);
              } else {
                resolve(resource.data);
              }
            } catch (e) {
              reject(e);
            }
          }),
        },
      };

      return enhancedResult;
    }
  } else if (result.meta?.response?.status === 401) {
    // do something here....
    console.error('401 error returned...');
  }

  return result;
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const emptySplitApi = createApi({
  baseQuery: asyncBaseQuery,
  endpoints: () => ({}),
});

export type AsyncOperationResponse = {
  entityId: number;
  entityType: string;
  correlationId: string;
  resourceUri: string;
  rowVersion?: number | null;
};

export function isAsyncOperation(res: unknown): res is AsyncOperationResponse {
  let respose = res as any;
  return (
    respose.correlationMessageType && respose.correlationId && respose.messageType && respose.entityId && respose.entityType && respose.resourceUri
  );
}

class LazyPromise<T> {
  private _executor: (resolve: (value: T) => any, reject: (reason: any) => any) => void;
  private _promise: Promise<T> | undefined;

  private get thePromise() {
    if (!this._promise) {
      this._promise = new Promise(this._executor);
    }
    return this._promise;
  }

  constructor(executor: (resolve: (value: T) => any, reject: (reason: any) => any) => void) {
    this._executor = executor;
  }

  then(onfulfilled?: ((value: T) => unknown) | null | undefined, onrejected?: ((reason: any) => PromiseLike<never>) | null | undefined) {
    return this.thePromise.then(onfulfilled, onrejected);
  }

  finally(onfinally?: (() => void) | null | undefined) {
    return this.thePromise.finally(onfinally);
  }

  catch(onrejected?: ((reason: any) => PromiseLike<never>) | null | undefined) {
    return this.thePromise.catch(onrejected);
  }
}
